import { useState } from "react";
import { Box, Button, Flex, HStack, Icon, Stack, VStack } from "@chakra-ui/react";
import { LuCalendarClock, LuUsers } from "react-icons/lu";
import CDS from "design-system";
import { Text14 } from "components/Text";
import { Heading18 } from "components/Heading";
import ScheduleMeetingForm from "features/Resources/Meeting/Components/Form/ScheduleMeetingForm";
import SelectFromMeetingList from "features/Resources/Meeting/Components/SelectFromMeetingList";
import { CalendarServiceEvent } from "features/Resources/Meeting/models";
import { BulkScheduleMeeting } from "../models";

type View = "menu" | "find_time" | "pick_session";

interface OptionCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  onClick: () => void;
}

function OptionCard({ icon, title, description, onClick }: OptionCardProps) {
  return (
    <Box
      as="button"
      w="100%"
      p="4"
      borderWidth="1px"
      borderRadius="md"
      borderColor="gray.200"
      _hover={{ borderColor: "blue.400", bg: "blue.50" }}
      transition="all 0.15s"
      textAlign="left"
      onClick={onClick}
    >
      <HStack spacing="3" align="flex-start">
        <Icon as={icon} boxSize="5" color="blue.500" mt="0.5" />
        <Box>
          <CDS.Text14 fontWeight="600">
            {title}
          </CDS.Text14>
          <Text14 color="gray.600">{description}</Text14>
        </Box>
      </HStack>
    </Box>
  );
}

interface BulkScheduleAdjustContentProps {
  meeting: BulkScheduleMeeting;
  isUpdatingReservation: boolean;
  onAdjustSubmit: (params: { scheduleAt: string; timezone: string }) => void;
  onSessionSelect: (params: { calendarEventId: string; startTime: string; endTime: string }) => void;
  onClose: () => void;
}

export default function BulkScheduleAdjustContent({
  meeting,
  isUpdatingReservation,
  onAdjustSubmit,
  onSessionSelect,
  onClose,
}: BulkScheduleAdjustContentProps) {
  const [view, setView] = useState<View>("menu");
  const meetingId = String(meeting.meetingId);

  if (view === "find_time") {
    return (
      <Stack>
        <Button variant="ghost" size="sm" alignSelf="flex-start" onClick={() => setView("menu")} mb="1">
          Back
        </Button>
        <Heading18>Find a New Time</Heading18>
        <ScheduleMeetingForm
          meetingId={meetingId}
          onSubmit={onAdjustSubmit}
          onComplete={onClose}
          submitButtonText="Update Time"
          submitIsLoading={isUpdatingReservation}
          initialDate={meeting.reservation?.startTime}
          reservationStartTime={meeting.reservation?.startTime}
        />
      </Stack>
    );
  }

  if (view === "pick_session") {
    return (
      <Stack>
        <Button variant="ghost" size="sm" alignSelf="flex-start" onClick={() => setView("menu")} mb="1">
          Back
        </Button>
        <Heading18>Pick Another Session</Heading18>
        <Text14 color="gray.600">Select an existing session to join:</Text14>
        <SelectFromMeetingList
          meetingId={meetingId}
          renderAction={(event: CalendarServiceEvent) => (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              isLoading={isUpdatingReservation}
              onClick={() =>
                onSessionSelect({
                  calendarEventId: event.id,
                  startTime: event.startTime,
                  endTime: event.endTime,
                })
              }
            >
              Select
            </Button>
          )}
        />
      </Stack>
    );
  }

  return (
    <VStack align="stretch" spacing="3" py={2}>
      <OptionCard
        icon={LuCalendarClock}
        title="Find a new time"
        description="Use the availability view to pick a different time for this session."
        onClick={() => setView("find_time")}
      />
      <OptionCard
        icon={LuUsers}
        title="Pick another session"
        description="Join a different existing session for this meeting."
        onClick={() => setView("pick_session")}
      />
      <Flex w="100%" justify="flex-end" mt="1">
        <Button onClick={onClose}>Close</Button>
      </Flex>
    </VStack>
  );
}
